/**
 * Incoming email, as Connect's staff run it.
 *
 * - **`InboundPricingCard`** (Email → Pricing): what one incoming email costs a
 *   client, how many one address may take in an hour, how long a message the
 *   wallet cannot pay for is kept. No price is no price at all: nothing is
 *   charged or processed until one is set.
 * - **`EmailInboundCard`** (a client's Services): whether the client has
 *   incoming email at all, and its own price.
 * - **`InboundRegister`** (Email → Incoming): every message, what became of
 *   it, the pipeline's health and the ledger's agreement — and, for one that
 *   failed after it was charged, trying it again (never a second charge) or
 *   refunding it (once).
 *
 * The API decides everything: whether a message may be retried or refunded,
 * whether a price is valid. Its words are shown where they apply.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, RefreshCw, Undo2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  DetailField,
  DetailGrid,
  Drawer,
  ErrorState,
  Input,
  LoadingState,
  ReadOnly,
  Select,
  ServerTable,
  StatCard,
  Switch,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDateTime,
  formatNumber,
  toast,
  useCan,
  type ServerColumn,
  type Tone,
} from '@connect/web-shared';

import { formatMetered } from './ai-card';
import { refusal } from './api-refusal';
import { EMAIL_ENDPOINT, emailKeys, type EmailClient, type EmailOverview, type EmailPricing } from './email';

export type { InboundPanel } from './email';

const INBOUND_ENDPOINT = `${EMAIL_ENDPOINT}/inbound`;
const MESSAGES_ENDPOINT = `${INBOUND_ENDPOINT}/messages`;

export const inboundKeys = {
  all: ['email', 'inbound'] as const,
  health: ['email', 'inbound', 'health'] as const,
  register: ['email', 'inbound', 'messages'] as const,
  message: (id: string) => ['email', 'inbound', 'message', id] as const,
};

/** Every status a message can be in, in staff's words. */
export const INBOUND_STATUS: Record<string, { label: string; tone: Tone }> = {
  received: { label: 'Received', tone: 'neutral' },
  rejected: { label: 'Refused', tone: 'neutral' },
  duplicate: { label: 'Duplicate', tone: 'neutral' },
  config_error: { label: 'Not configured', tone: 'warning' },
  awaiting_funds: { label: 'Awaiting funds', tone: 'warning' },
  charged: { label: 'Charged', tone: 'info' },
  processing: { label: 'Processing', tone: 'info' },
  retry: { label: 'Retrying', tone: 'warning' },
  completed: { label: 'Completed', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  reconcile: { label: 'Reconcile', tone: 'danger' },
};

const REASONS: Record<string, string> = {
  unknown_address: 'Unknown address',
  revoked_address: 'Revoked address',
  not_available: 'Not available to the client',
  not_enabled: 'Switched off by the client',
  no_service: 'No email service',
  spam: 'Spam',
  virus: 'Virus',
  too_large: 'Too large',
  malformed: 'Malformed',
  unreadable: 'Could not be fetched',
  auto_generated: 'Automatic message',
  loop: 'Forwarding loop',
  rate_limited: 'Hourly cap',
  insufficient_balance: 'Wallet too low',
  expired_unfunded: 'Held too long unpaid',
  duplicate: 'Same message earlier',
  no_price: 'No price set',
  unavailable: 'Processing unavailable',
  processing_failed: 'Processing failed',
};


interface InboundStaffRow {
  public_id: string;
  created_at: string;
  sender: string;
  sender_name: string;
  subject: string;
  status: string;
  reason: string | null;
  rate: string | null;
  attempts: number;
  client_public_id: string | null;
  client_name: string | null;
}

interface InboundStaffDetail {
  public_id: string;
  created_at: string;
  status: string;
  reason_code: string | null;
  reason: string | null;
  last_error: string | null;
  attempts: number;
  next_attempt_at: string | null;
  sender: string;
  sender_name: string;
  envelope_from: string;
  recipient: string;
  subject: string;
  rfc_message_id: string | null;
  object_key: string;
  provider_message_id: string;
  size_bytes: number | null;
  verdicts: Record<string, string>;
  price: string | null;
  charged_at: string | null;
  refunded: boolean;
  completed_at: string | null;
  ai_model: string | null;
  ai_tokens: number;
  result: { summary: string; category: string; urgency: string } | null;
  ticket: { public_id: string; reference: string } | null;
  client: { public_id: string; name: string } | null;
  charge_entry: { public_id: string; amount: string; balance_after: string } | null;
  refund_entry: { public_id: string; amount: string } | null;
}

interface InboundHealth {
  offered: boolean;
  missing_settings: string[];
  domain: string | null;
  last_24_hours: Record<string, number>;
  open: Record<string, number>;
  oldest_unfinished_charge_at: string | null;
  average_seconds_to_complete: number | null;
  queue: { waiting: number; in_flight: number } | null;
  reconciliation: { unpaid: number; orphan_entries: number; mismatched: number; stuck: number };
}

const message = (error: unknown) => (error instanceof ApiError ? error.message : undefined);

// ── the platform's price ────────────────────────────────────
export function InboundPricingCard({ pricing }: { pricing: EmailPricing }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const saved = {
    inbound_price: pricing.inbound_price ?? '',
    inbound_hourly_cap: String(pricing.inbound_hourly_cap),
    inbound_hold_days: String(pricing.inbound_hold_days),
    inbound_included_mb: String(pricing.inbound_included_mb),
    inbound_price_per_extra_mb: pricing.inbound_price_per_extra_mb,
  };
  const [draft, setDraft] = useState(saved);
  const changed = (Object.keys(saved) as (keyof typeof saved)[]).some((key) => draft[key] !== saved[key]);
  const save = useMutation({
    // Only these fields: the API keeps what is not sent.
    mutationFn: () =>
      api.put<EmailOverview>(`${EMAIL_ENDPOINT}/pricing`, {
        inbound_price: draft.inbound_price === '' ? null : draft.inbound_price,
        inbound_hourly_cap: Number(draft.inbound_hourly_cap),
        inbound_hold_days: Number(draft.inbound_hold_days),
        inbound_included_mb: Number(draft.inbound_included_mb),
        inbound_price_per_extra_mb: draft.inbound_price_per_extra_mb,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(emailKeys.overview, data);
      toast.success('Incoming email prices saved', 'Emails accepted from now on are charged at them.');
    },
  });
  const refused = refusal(save.error);
  const set = (field: keyof typeof draft, value: string) => {
    save.reset();
    setDraft((was) => ({ ...was, [field]: value.trim() }));
  };
  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title="Incoming email"
          description="Charged once for each incoming email accepted for processing. A client's own price, set on its page, wins. Left empty, nothing is charged and nothing is processed."
        />
        <CardBody className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label="Price per incoming email (₹)"
              inputMode="decimal"
              value={draft.inbound_price}
              placeholder="Not priced"
              hint="Zero is free. Empty is no price."
              error={refused?.fields.inbound_price}
              onChange={(event) => set('inbound_price', event.target.value)}
            />
            <Input
              label="Emails per address an hour"
              inputMode="numeric"
              value={draft.inbound_hourly_cap}
              hint="More are refused, uncharged: a loop or a flood stops here."
              error={refused?.fields.inbound_hourly_cap}
              onChange={(event) => set('inbound_hourly_cap', event.target.value)}
            />
            <Input
              label="Days a short wallet holds mail"
              inputMode="numeric"
              value={draft.inbound_hold_days}
              hint="For clients who chose to keep mail until they top up."
              error={refused?.fields.inbound_hold_days}
              onChange={(event) => set('inbound_hold_days', event.target.value)}
            />
            <Input
              label="Size the price covers (MB)"
              inputMode="numeric"
              value={draft.inbound_included_mb}
              hint="As received, attachments included."
              error={refused?.fields.inbound_included_mb}
              onChange={(event) => set('inbound_included_mb', event.target.value)}
            />
            <Input
              label="Each extra MB (₹)"
              inputMode="decimal"
              value={draft.inbound_price_per_extra_mb}
              hint="Added for each started MB past that size. Zero adds nothing."
              error={refused?.fields.inbound_price_per_extra_mb}
              onChange={(event) => set('inbound_price_per_extra_mb', event.target.value)}
            />
          </div>
          {refused?.general.length ? <Alert tone="danger">{refused.general.join(' ')}</Alert> : null}
          {canEdit && changed ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  save.reset();
                  setDraft(saved);
                }}
              >
                Discard
              </Button>
              <Button loading={save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </ReadOnly>
  );
}

// ── one client ──────────────────────────────────────────────
export function EmailInboundCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const url = `${EMAIL_ENDPOINT}/clients/${clientPublicId}`;
  const key = emailKeys.client(clientPublicId);
  const email = useQuery({ queryKey: key, queryFn: () => api.get<EmailClient>(url) });
  const [own, setOwn] = useState<boolean | null>(null);
  const [price, setPrice] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch<EmailClient>(url, body),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      setOwn(null);
      setPrice(null);
      toast.success('Saved');
    },
    onError: (error) => toast.error('Not saved', message(error)),
  });

  if (email.isLoading) return <LoadingState label="Loading incoming email" />;
  if (email.isError || !email.data) {
    return <ErrorState message={message(email.error)} onRetry={() => void email.refetch()} />;
  }
  const inbound = email.data.inbound;
  const ownChosen = own ?? inbound.own_price !== null;
  const ownValue = price ?? inbound.own_price ?? inbound.platform_price ?? '';
  const counts = inbound.counts_30_days;

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Inbox className="size-4" />
              Incoming email
            </span>
          }
          description={`Mail ${clientName} forwards to its Connect addresses, read and sorted for it, and charged to its email wallet.`}
        />
        <CardBody className="space-y-4">
          {!inbound.offered ? (
            <Alert tone="info">This installation does not offer incoming email: its settings are not all set.</Alert>
          ) : null}
          <Switch
            label="Available to this client"
            hint={
              inbound.allowed
                ? `${clientName} sees its incoming email screen. It switches processing on itself.`
                : 'Off: the client has no incoming email screen, and mail to its addresses is refused uncharged.'
            }
            checked={inbound.allowed} disabled={update.isPending}
            onChange={(event) =>
              update.mutate(
                event.target.checked
                  ? { inbound_allowed: true, inbound_note: null }
                  : { inbound_allowed: false, inbound_note: note.trim() || null },
              )
            }
          />
          {inbound.allowed && canEdit ? (
            <Input
              size="sm"
              label="Why, if you turn it off (staff only)"
              value={note}
              maxLength={200}
              containerClassName="max-w-md"
              onChange={(event) => setNote(event.target.value)}
            />
          ) : null}
          {!inbound.allowed && inbound.note ? <p className="text-xs text-muted">Turned off: {inbound.note}</p> : null}
  
          <div className="grid gap-3 sm:grid-cols-4">
            <StatCard label="Charged per email" value={inbound.price !== null ? formatMetered(inbound.price) : 'Not priced'} />
            <StatCard label="Client's switch" value={inbound.enabled ? 'On' : 'Off'} />
            <StatCard label="Addresses" value={formatNumber(inbound.addresses)} />
            <StatCard
              label="Processed, 30 days"
              value={formatNumber(counts.completed ?? 0)}
              hint={`${formatNumber(counts.failed ?? 0)} failed · ${formatNumber(counts.awaiting_funds ?? 0)} awaiting funds`}
            />
          </div>
  
          <div className="space-y-2 rounded border border-line p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-medium text-muted">Price per incoming email (₹, before GST)</p>
              <Select
                size="sm"
                containerClassName="w-64"
                aria-label="Incoming email price"
                value={ownChosen ? 'own' : 'platform'}
                options={[
                  { value: 'platform', label: "The platform's price" },
                  { value: 'own', label: 'Its own price' },
                ]}
                onChange={(event) => setOwn(event.target.value === 'own')}
              />
            </div>
            {ownChosen ? (
              <Input
                size="sm"
                label="Its own price"
                inputMode="decimal"
                containerClassName="max-w-xs"
                value={ownValue}
                hint={
                  inbound.platform_price !== null
                    ? `The platform charges ${formatMetered(inbound.platform_price)}.`
                    : 'The platform has no price for incoming email.'
                }
                onChange={(event) => setPrice(event.target.value.trim())}
              />
            ) : (
              <p className="text-xs text-muted">
                {inbound.platform_price !== null
                  ? `${formatMetered(inbound.platform_price)} an email — set under Email → Pricing.`
                  : 'No platform price yet — set it under Email → Pricing, or give this client its own.'}
              </p>
            )}
            {canEdit && (own !== null || price !== null) ? (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  loading={update.isPending}
                  onClick={() => update.mutate({ inbound_price: ownChosen ? ownValue : null })}
                >
                  Save the price
                </Button>
              </div>
            ) : null}
          </div>
        </CardBody>
      </Card>
    </ReadOnly>
  );
}

// ── every message ───────────────────────────────────────────
export function InboundRegister() {
  const [open, setOpen] = useState<string | null>(null);
  const health = useQuery({
    queryKey: inboundKeys.health,
    queryFn: () => api.get<InboundHealth>(`${INBOUND_ENDPOINT}/health`),
  });
  const fetchRows = useMemo(() => createServerFetcher<InboundStaffRow>(MESSAGES_ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(MESSAGES_ENDPOINT), []);
  const columns = useMemo<ServerColumn<InboundStaffRow>[]>(
    () => [
      { field: 'created_at', header: 'Received', filter: 'date', width: 170, render: (row) => formatDateTime(row.created_at) },
      {
        field: 'client_name',
        header: 'Client',
        filter: 'text',
        minWidth: 160,
        render: (row) => row.client_name ?? <span className="text-muted">Nobody</span>,
      },
      {
        field: 'sender',
        header: 'From',
        filter: 'text',
        minWidth: 200,
        render: (row) => <span className="truncate">{row.sender_name ? `${row.sender_name} <${row.sender}>` : row.sender}</span>,
      },
      { field: 'subject', header: 'Subject', filter: 'text', minWidth: 220, render: (row) => <span className="truncate">{row.subject}</span> },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: Object.entries(INBOUND_STATUS).map(([value, { label }]) => ({ value, label })),
        width: 150,
        render: (row) => {
          const status = INBOUND_STATUS[row.status] ?? { label: row.status, tone: 'neutral' as Tone };
          return <Badge tone={status.tone}>{status.label}</Badge>;
        },
      },
      {
        field: 'reason',
        header: 'Why',
        filter: 'select',
        options: Object.entries(REASONS).map(([value, label]) => ({ value, label })),
        width: 180,
        render: (row) => (row.reason ? REASONS[row.reason] ?? row.reason : <span className="text-muted">—</span>),
      },
      { field: 'attempts', header: 'Attempts', filter: 'number', width: 110, align: 'right', render: (row) => formatNumber(row.attempts) },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      {health.data ? <HealthStrip health={health.data} /> : health.isError ? <ErrorState onRetry={() => void health.refetch()} /> : null}
      <ServerTable<InboundStaffRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={inboundKeys.register}
        stateKey="email.inbound.staff"
        searchPlaceholder="Search sender, subject or client"
        viewField="subject"
        onView={(row) => setOpen(row.public_id)}
        emptyTitle="No incoming email"
        emptyDescription="Mail forwarded to clients' Connect addresses appears here."
      />
      <InboundDrawer id={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function HealthStrip({ health }: { health: InboundHealth }) {
  const recon = health.reconciliation;
  const disagreements = recon.unpaid + recon.orphan_entries + recon.mismatched;
  const day = health.last_24_hours;
  return (
    <>
      {!health.offered ? (
        <Alert tone="warning" title="Incoming email is not offered">
          {health.missing_settings.length
            ? `Missing settings: ${health.missing_settings.join(', ')}.`
            : 'INBOUND_EMAIL_ENABLED is off.'}
        </Alert>
      ) : null}
      {disagreements > 0 ? (
        <Alert tone="danger" title="The ledger and the records disagree">
          {recon.unpaid} past the charge with no ledger entry · {recon.orphan_entries} ledger entries no message points at ·{' '}
          {recon.mismatched} charged a different amount. See the deployment guide's reconciliation steps.
        </Alert>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard
          label="Completed, 24 hours"
          value={formatNumber(day.completed ?? 0)}
          hint={
            health.average_seconds_to_complete !== null
              ? `${health.average_seconds_to_complete} s on average`
              : undefined
          }
        />
        <StatCard label="Refused, 24 hours" value={formatNumber((day.rejected ?? 0) + (day.duplicate ?? 0))} />
        <StatCard
          label="Needs attention"
          value={formatNumber((health.open.failed ?? 0) + (health.open.reconcile ?? 0))}
          tone={(health.open.failed ?? 0) + (health.open.reconcile ?? 0) > 0 ? 'warning' : 'neutral'}
          hint={`${formatNumber(recon.stuck)} charged over an hour ago, unfinished`}
        />
        <StatCard
          label="Queue"
          value={health.queue ? formatNumber(health.queue.waiting) : '—'}
          hint={health.queue ? `${formatNumber(health.queue.in_flight)} being read` : 'Not reachable'}
        />
      </div>
    </>
  );
}

function InboundDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const canRefund = useCan('messaging.email.create');
  const [confirming, setConfirming] = useState<'retry' | 'refund' | null>(null);
  const detail = useQuery({
    queryKey: inboundKeys.message(id ?? ''),
    queryFn: () => api.get<InboundStaffDetail>(`${MESSAGES_ENDPOINT}/${id}`),
    enabled: id !== null,
  });
  const act = useMutation({
    mutationFn: (what: 'retry' | 'refund') => api.post<InboundStaffDetail>(`${MESSAGES_ENDPOINT}/${id}/${what}`),
    onSuccess: (data, what) => {
      setConfirming(null);
      queryClient.setQueryData(inboundKeys.message(data.public_id), data);
      void queryClient.invalidateQueries({ queryKey: inboundKeys.all });
      toast.success(what === 'retry' ? 'It will be processed again' : 'Refunded');
    },
    onError: (error) => {
      setConfirming(null);
      toast.error('Not done', message(error));
    },
  });
  const data = detail.data;
  const actionable = data && (data.status === 'failed' || data.status === 'reconcile') && !data.refunded;

  return (
    <Drawer
      open={id !== null}
      onClose={onClose}
      title={data?.subject || 'Incoming email'}
      width="lg"
      footer={
        actionable ? (
          <div className="flex justify-end gap-2">
            {canRefund && data.charge_entry ? (
              <Button variant="ghost" onClick={() => setConfirming('refund')}>
                <Undo2 className="size-4" /> Refund
              </Button>
            ) : null}
            {canEdit && data.charge_entry ? (
              <Button onClick={() => setConfirming('retry')}>
                <RefreshCw className="size-4" /> Try again
              </Button>
            ) : null}
          </div>
        ) : undefined
      }
    >
      {detail.isLoading ? (
        <LoadingState label="Loading" />
      ) : detail.isError || !data ? (
        <ErrorState message={message(detail.error)} onRetry={() => void detail.refetch()} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge tone={INBOUND_STATUS[data.status]?.tone ?? 'neutral'}>{INBOUND_STATUS[data.status]?.label ?? data.status}</Badge>
            {data.reason_code ? <Badge tone="neutral">{REASONS[data.reason_code] ?? data.reason_code}</Badge> : null}
            {data.refunded ? <Badge tone="success">Refunded</Badge> : null}
          </div>
          {data.last_error ? <Alert tone="warning">{data.last_error}</Alert> : null}
          <DetailGrid>
            <DetailField label="Client">{data.client?.name ?? 'Nobody (unknown address)'}</DetailField>
            <DetailField label="Delivered to">
              <span className="break-all">{data.recipient}</span>
            </DetailField>
            <DetailField label="From (claimed)">
              <span className="break-all">{data.sender_name ? `${data.sender_name} <${data.sender}>` : data.sender}</span>
            </DetailField>
            <DetailField label="Envelope sender">
              <span className="break-all">{data.envelope_from || '(empty)'}</span>
            </DetailField>
            <DetailField label="Checks">
              {Object.entries(data.verdicts)
                .map(([name, value]) => `${name.toUpperCase()} ${value || '—'}`)
                .join(' · ')}
            </DetailField>
            <DetailField label="Size">{data.size_bytes !== null ? `${formatNumber(data.size_bytes)} bytes` : '—'}</DetailField>
            <DetailField label="Charged">
              {data.charge_entry
                ? `${formatMetered(data.price ?? '0')} at ${formatDateTime(data.charged_at)} — balance after ${formatMetered(data.charge_entry.balance_after)}`
                : 'Not charged'}
            </DetailField>
            <DetailField label="Attempts">
              {formatNumber(data.attempts)}
              {data.next_attempt_at ? ` · next ${formatDateTime(data.next_attempt_at)}` : ''}
            </DetailField>
            <DetailField label="Stored as">
              <span className="break-all font-mono text-xs">{data.object_key}</span>
            </DetailField>
            <DetailField label="Message-ID">
              <span className="break-all font-mono text-xs">{data.rfc_message_id ?? '—'}</span>
            </DetailField>
            <DetailField label="Model">
              {data.ai_model ? `${data.ai_model} · ${formatNumber(data.ai_tokens)} tokens` : '—'}
            </DetailField>
            <DetailField label="Ticket">{data.ticket?.reference ?? '—'}</DetailField>
          </DetailGrid>
          {data.result ? (
            <Card>
              <CardBody>
                <p className="text-sm">{data.result.summary}</p>
                <p className="mt-1 text-xs text-muted">
                  {data.result.category} · {data.result.urgency}
                </p>
              </CardBody>
            </Card>
          ) : null}
        </div>
      )}
      <Confirm
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming) act.mutate(confirming);
        }}
        title={confirming === 'refund' ? 'Refund this email?' : 'Process this email again?'}
        message={
          confirming === 'refund'
            ? 'What it cost goes back to the client’s email wallet, once. A refunded email is not processed again.'
            : 'It is processed again with its one charge — it is not charged a second time.'
        }
        confirmLabel={confirming === 'refund' ? 'Refund' : 'Try again'}
      />
    </Drawer>
  );
}
